import type { WalletUnlockGrant } from './wallet-unlock-session';

export const WALLET_GRANT_STORAGE_KEY = 'gamma.wallet-unlock.v1';

export interface WalletGrantStore {
  read(): WalletUnlockGrant | null;
  write(grant: WalletUnlockGrant): void;
  clear(): void;
  subscribe(listener: () => void): () => void;
}

const isGrant = (value: unknown): value is WalletUnlockGrant => {
  const grant = value as Partial<WalletUnlockGrant> | null;

  return (
    typeof grant?.gammaUserId === 'string' &&
    typeof grant.privyUserId === 'string' &&
    typeof grant.lastActivityAt === 'number'
  );
};

const parseGrant = (raw: string | null): WalletUnlockGrant | null => {
  if (!raw) {
    return null;
  }

  try {
    const value: unknown = JSON.parse(raw);
    return isGrant(value) ? value : null;
  } catch {
    return null;
  }
};

const toStoredGrant = ({ gammaUserId, privyUserId, lastActivityAt }: WalletUnlockGrant): string =>
  JSON.stringify({ gammaUserId, privyUserId, lastActivityAt });

export class BrowserWalletGrantStore implements WalletGrantStore {
  private memory: WalletUnlockGrant | null = null;
  private storageAvailable = true;

  read(): WalletUnlockGrant | null {
    return this.withStorage(
      (storage) => parseGrant(storage.getItem(WALLET_GRANT_STORAGE_KEY)),
      () => this.memory,
    );
  }

  write(grant: WalletUnlockGrant): void {
    this.memory = grant;
    this.withStorage(
      (storage) => storage.setItem(WALLET_GRANT_STORAGE_KEY, toStoredGrant(grant)),
      () => undefined,
    );
  }

  clear(): void {
    this.memory = null;
    this.withStorage(
      (storage) => storage.removeItem(WALLET_GRANT_STORAGE_KEY),
      () => undefined,
    );
  }

  subscribe(listener: () => void): () => void {
    const onStorage = (event: StorageEvent) => {
      if (event.key === WALLET_GRANT_STORAGE_KEY || event.key === null) {
        listener();
      }
    };

    window.addEventListener('storage', onStorage);

    return () => window.removeEventListener('storage', onStorage);
  }

  private withStorage<T>(action: (storage: Storage) => T, fallback: () => T): T {
    if (!this.storageAvailable) {
      return fallback();
    }

    try {
      return action(window.localStorage);
    } catch {
      // Blocked site data or a full quota makes browser storage throw; the grant then lives only
      // in this tab, as it did before persistence, until the page reloads.
      this.storageAvailable = false;
      return fallback();
    }
  }
}
